"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { markdownHref, siteOrigin } from "@/lib/developer-markdown";
import { Icon } from "../_family/Icon";
import { pinned } from "./build";
import { OPEN_IN, askAbout } from "./open-in";

/** The page's Markdown as text, from this page's own build where it has one (as a route's fields are fetched). */
async function markdownOf(src: string): Promise<string> {
  const res = await fetch(pinned(src));
  if (!res.ok) throw new Error(`${src}: HTTP ${res.status}`);
  return res.text();
}

/**
 * Copies a page's Markdown. The text is still on its way when the click ends, so where the browser takes a
 * clipboard item it is handed the text to come (Safari keeps a click's permission only for a write begun in
 * it); anywhere else the text is written once it arrives.
 */
export async function copyMarkdown(src: string): Promise<void> {
  // No clipboard, no fetch: a read nobody awaits would reject unheard.
  const clipboard = navigator.clipboard;
  if (!clipboard) throw new Error("No clipboard.");
  const text = markdownOf(src);
  if (typeof ClipboardItem !== "undefined" && clipboard.write) {
    try {
      await clipboard.write([
        new ClipboardItem({
          "text/plain": text.then((t) => new Blob([t], { type: "text/plain" })),
        }),
      ]);
      return;
    } catch {
      // A browser whose clipboard item takes no text to come: write it as text below.
    }
  }
  await clipboard.writeText(await text);
}

const SAID = {
  copied: "Copied",
  failed: "Could not copy",
} as const;

/**
 * Every docs page's menu (agent-readable docs): copy the page as Markdown, for a coding agent, open its
 * Markdown, or open a new conversation about it in a third-party assistant. "Copy page" copies at once; the
 * caret opens them all, as the WAI-ARIA menu button does: Enter, Space or ArrowDown opens it on the first
 * entry and ArrowUp on the last; inside, arrows move, Home and End jump, Escape closes it back to the caret,
 * and Tab closes it and moves on. The entries are out of the tab sequence (tabIndex -1): the script moves
 * focus among them, so Tab from any of them leaves the menu.
 */
export function PageMenu() {
  const path = usePathname() ?? "";
  const href = markdownHref(path);
  const prompt = askAbout(`${siteOrigin()}${href}`);
  const [open, setOpen] = useState(false);
  // Which entry takes the focus when the menu opens: the first, or the last (the caret's ArrowUp).
  const [start, setStart] = useState<"first" | "last">("first");
  const [said, setSaid] = useState<keyof typeof SAID | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const items = useRef<Array<HTMLElement | null>>([]);

  useEffect(() => {
    if (!said) return;
    const t = setTimeout(() => setSaid(null), 1600);
    return () => clearTimeout(t);
  }, [said]);

  useEffect(() => {
    if (!open) return;
    items.current[start === "last" ? items.current.length - 1 : 0]?.focus();
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("click", away);
    return () => document.removeEventListener("click", away);
  }, [open, start]);

  const copy = () => {
    copyMarkdown(href).then(
      () => setSaid("copied"),
      () => setSaid("failed")
    );
  };

  const close = () => {
    setOpen(false);
    button.current?.focus();
  };

  const openOn = (at: "first" | "last") => {
    setStart(at);
    setOpen(true);
  };

  const onCaretKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    openOn(e.key === "ArrowDown" ? "first" : "last");
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const n = items.current.length;
    const i = items.current.findIndex((el) => el === document.activeElement);
    const to = (k: number) => {
      e.preventDefault();
      items.current[k]?.focus();
    };
    if (e.key === "ArrowDown") to((i + 1) % n);
    else if (e.key === "ArrowUp") to((i + n - 1) % n);
    else if (e.key === "Home") to(0);
    else if (e.key === "End") to(n - 1);
    else if (e.key === "Escape") close();
    else if (e.key === "Tab") setOpen(false);
  };

  return (
    <div className={open ? "pmenu open" : "pmenu"} ref={wrap}>
      <button
        className={said === "copied" ? "pm-copy did" : "pm-copy"}
        type="button"
        onClick={() => {
          setOpen(false);
          copy();
        }}
      >
        <Icon name={said === "copied" ? "check" : "copy"} />
        <span className="lbl">{said ? SAID[said] : "Copy page"}</span>
      </button>
      <button
        ref={button}
        className="pm-more"
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="vf-page-menu"
        aria-label="More ways to read this page"
        onClick={() => (open ? setOpen(false) : openOn("first"))}
        onKeyDown={onCaretKey}
      >
        <Icon name="caretDown" />
      </button>
      <div className="pm-pop">
        <div
          className="pm-menu"
          id="vf-page-menu"
          role="menu"
          aria-label="This page"
          onKeyDown={onKey}
        >
          <button
            ref={(el) => {
              items.current[0] = el;
            }}
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={() => {
              copy();
              close();
            }}
          >
            <span className="t">
              <Icon name="copy" />
            </span>
            <span>
              <b>Copy page</b>
              <small>As Markdown, for your coding agent</small>
            </span>
          </button>
          <a
            ref={(el) => {
              items.current[1] = el;
            }}
            href={href}
            role="menuitem"
            tabIndex={-1}
            onClick={() => setOpen(false)}
          >
            <span className="t">
              <Icon name="fileText" />
            </span>
            <span>
              <b>View as Markdown</b>
              <small>This page as plain text</small>
            </span>
          </a>
          {OPEN_IN.map((to, i) => (
            <a
              key={to.name}
              ref={(el) => {
                items.current[2 + i] = el;
              }}
              href={to.url(prompt)}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              tabIndex={-1}
              onClick={() => setOpen(false)}
            >
              <span className="t">
                <Icon name="chatCircle" />
              </span>
              <span>
                <b>Open in {to.name}</b>
                <small>Ask about this page, in a new tab</small>
              </span>
              <Icon name="arrowSquareOut" className="ext" />
            </a>
          ))}
        </div>
      </div>
      <span className="sr" aria-live="polite">
        {said === "copied" ? "Page copied" : said ? SAID[said] : ""}
      </span>
    </div>
  );
}
